import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { ID } from '@datorama/akita';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, combineLatest, filter, Observable, startWith, Subscription } from 'rxjs';
import { Category, CustomerDetails, PromotionCode, Shipping, ShippingInfo } from 'src/app/models';
import { GtmService } from 'src/app/services/analytics/gtm.service';
import { MParticleService } from 'src/app/services/analytics/mparticle.service';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { BasketService } from 'src/app/services/basket/basket.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { CustomerDetailService } from 'src/app/services/checkout/customer-details.service';
import { ContentService } from 'src/app/services/content/content.service';
import { ShippingDialogService } from 'src/app/services/dialog/shipping-dialog.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ShippingService } from 'src/app/services/shipping';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { TypedFormGroup } from 'src/app/shared/forms';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';

const errorMessageContent =
  'Et eller flere af de produkter, du har lagt i kurven, gør at du ikke kan gå til betaling. Produkterne der er fremhævet med en rød kant, kan ikke købes med den udleveringstype, du har valgt. Du kan prøve at fjerne produktet med den røde kant, eller prøve at skifte udleveringstype. Hvis du ønsker det, kan du klikke på knappen herunder, og så fjerner systemet de produkter, der gør du ikke kan gå til betaling.';

@Component({
  selector: 'app-basket-utilty',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasketOverviewUtility extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  protected categoryService = inject(CategoryService);
  protected checkoutService = inject(CheckoutService);
  protected shippingDialogService = inject(ShippingDialogService);
  protected shippingService = inject(ShippingService);
  protected router = inject(Router);
  protected basketQuery = inject(BasketQuery);
  protected basketService = inject(BasketService);
  protected cdr = inject(ChangeDetectorRef);
  protected deviceService = inject(DeviceDetectorService);
  protected customerDetailService = inject(CustomerDetailService);
  protected themeService = inject(ThemeService);
  protected kioskService = inject(KioskService);
  protected elRef = inject(ElementRef);
  protected gtmService = inject(GtmService);
  private mParticle = inject(MParticleService, { optional: true });

  subscription: Subscription;

  @ViewChild('title', { read: ElementRef }) titleRef!: ElementRef<HTMLElement>;

  form!: TypedFormGroup<CustomerDetails>;

  totalPrice$: Observable<number>;
  linesTotalPrice$: Observable<number>;
  packagingPrice$: Observable<number>;
  discount$: Observable<number>;
  giftCardDiscount$: Observable<number>;
  giftCardRemainingValue$: Observable<number>;
  containsAlcohol$: Observable<boolean>;
  shipping$: Observable<Shipping>;
  shippingValid$: BehaviorSubject<boolean>;
  missingBasketTotal$: BehaviorSubject<number>;
  isValidState: boolean;
  isLoading$: Observable<boolean>;
  useAgeVerification: boolean;
  shippingInfo$: Observable<ShippingInfo>;
  disableNote: boolean;

  checkoutUrl = `/${environment.urls.checkout}`;
  locale = environment.i18n.language;
  currency = environment.i18n.currency;

  storeClosed: boolean;
  isMobile: boolean;
  isKiosk: boolean;
  useKioskShipping: boolean;

  basketTitle: string;
  checkoutButton: string;

  totalProductsPriceLabel: string;
  packagingPriceLabel: string;
  shippingPickupPriceLabel: string;
  shippingPickupButton: string;
  shippingPriceLabel: string;
  shippingPriceDeliveryLabel: string;
  contactInformation: string;
  contactPhoneNumber: string;
  totalPriceLabel: string;
  ageRestrictedLabel: string;

  promotionCodeLabel: string;
  promotionCodePlaceholder: string;
  promotionCodeButton: string;
  promotionForm: TypedFormGroup<PromotionCode>;
  discountPriceLabel: string;
  giftCardDiscountPriceLabel: string;
  giftCardRemainingValuePriceLabel: string;
  giftCardEnabled: boolean;

  productHasErrors: boolean;
  showProductErrors: boolean;
  validSubscription: Subscription;
  errorMessage: {
    title: string;
    message: string;
    button: string;
  };

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'basketOverview', { meta: true, noIndex: true });

    this.subscription = new Subscription();

    const sharedContent = this.contentService.getSharedContent();
    this.totalPriceLabel = sharedContent.get('totalPriceLabelText');

    this.storeClosed = this.contentService.getStoreClosed();

    this.basketTitle = this.content.get('basketTitleLabelText');
    this.checkoutButton = this.content.get('checkoutButtonLabel');

    this.totalProductsPriceLabel = this.content.get('totalItemsLabelText');
    this.packagingPriceLabel = this.content.get('packagingPriceLabelText');
    this.shippingPriceLabel = this.content.get('shippingPriceLabel');
    this.shippingPriceDeliveryLabel = this.content.get('shippingPriceDeliveryLabel');
    this.discountPriceLabel = this.content.get('discountPriceLabelText') || 'Besparelse';
    this.giftCardDiscountPriceLabel = this.content.get('giftCardDiscountPriceLabel') || 'Gavekort besparelse'; // TODO:
    this.giftCardRemainingValuePriceLabel = this.content.get('giftCardRemainingValuePriceLabel') || 'Beløb tilbage på gavekort'; // TODO:

    this.shippingPickupPriceLabel = this.content.get('shippingPickupPriceLabelText');
    this.shippingPickupButton = this.content.get('shippingPickupButtonText');

    this.ageRestrictedLabel =
      this.content.get('ageRestrictedText') ||
      'Da du har tilføjet varer til din kurv med aldersbegrænsning, bedes du tilkendegive, at du er 18 år eller ældre';

    this.contactInformation = this.content.get('contactInformation');
    this.contactPhoneNumber = this.content.get('contactPhoneNumber');

    this.promotionCodeLabel = this.content.get('promotionCodeLabelText') || 'Rabatkode';
    this.promotionCodePlaceholder = this.content.get('promotionPlaceholderText') || 'Indtast rabatkode';
    this.promotionCodeButton = this.content.get('promotionCodeButtonText') || 'Check';

    this.errorMessage = {
      title: this.content.get('errorMessageTitle') || 'Justering Nødvendig',
      message: this.content.get('errorMessageContent') || errorMessageContent,
      button: this.content.get('errorMessageButtonText') || 'Juster min Kurv',
    };
  }

  ngOnInit() {
    this.productHasErrors = false;
    this.showProductErrors = false;

    this.isMobile = this.deviceService.isMobile();
    this.isKiosk = this.kioskService.isKiosk;
    this.useKioskShipping = this.kioskService.useKioskShipping;

    this.isLoading$ = this.basketQuery.selectLoading();

    this.totalPrice$ = this.basketQuery.selectTotal$;
    this.linesTotalPrice$ = this.basketQuery.selectLinesTotal$;
    this.packagingPrice$ = this.basketQuery.selectPackagingPrice$;
    this.discount$ = this.basketQuery.selectDiscount$;
    this.giftCardDiscount$ = this.basketQuery.selectGiftCardDiscount$;
    this.giftCardRemainingValue$ = this.basketQuery.selectRemainingGiftCardValue$;
    this.containsAlcohol$ = this.basketQuery.selectContainsAlcohol$;
    this.shipping$ = this.shippingService.getShipping();
    this.shippingValid$ = this.shippingService.getShippingValid();
    this.missingBasketTotal$ = this.shippingService.getMissingBasketTotal();
    this.shippingInfo$ = this.shippingService.getSelectedShippingInfo();

    this.form = this.customerDetailService.getForm();

    const merchant = this.contentService.getMerchant();
    this.giftCardEnabled = merchant.enableGiftCards;
    this.useAgeVerification = merchant.useAgeVerification;
    this.disableNote = merchant.disableOrderNote;

    if (this.useKioskShipping) this.shippingService.loadKioskShipping();

    this.subscription.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
        this.checkoutService.isCheckingOut = true;
      }),
    );

    if (merchant.enableDiscountCodes) {
      this.subscription.add(
        this.basketQuery.selectDiscountCode$.subscribe((discountCode) => {
          this.promotionForm = this.basketService.getPromotionForm(discountCode);
        }),
      );
    }

    this.subscription.add(
      this.basketQuery.selectErrorProductIds$.subscribe((ids) => {
        this.productHasErrors = ids.length > 0;
        if (this.showProductErrors && ids.length === 0) this.showProductErrors = false;
      }),
    );

    this.subscription.add(
      combineLatest([
        this.basketQuery.selectProductLines$,
        this.shippingValid$,
        this.containsAlcohol$,
        this.form.controls.legalAgeConsent.valueChanges.pipe(startWith(this.form.controls.legalAgeConsent.value)),
        this.missingBasketTotal$,
      ]).subscribe(([productLines, shippingValid, containsAlcohol, legalAgeConsent, missingBasketTotal]) => {
        this.isValidState =
          productLines.length > 0 &&
          shippingValid &&
          (!this.useAgeVerification && containsAlcohol ? legalAgeConsent : true) &&
          missingBasketTotal === 0;
        this.cdr.detectChanges();
      }),
    );

    this.mParticle?.viewBasket();
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getCategoryById(id: number): Category {
    return this.categoryService.getById(id)?.data;
  }

  openShippingDialog() {
    this.shippingDialogService.openShippingDialog();
  }

  getSelectedShipping(id: ID) {
    return this.shippingService.getSelectedShipping(id);
  }

  startCheckout() {
    this.checkoutService.isCheckingOut = true;
  }

  async checkout() {
    this.validSubscription?.unsubscribe();
    this.validateBasket();

    if (this.productHasErrors) {
      this.titleRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    const errors = this.basketQuery.getErrors();
    if (errors.length) {
      if (
        errors[0].responseCode === this.basketService.shippingErrorCode &&
        this.kioskService.useKioskShipping &&
        !this.shippingService.kioskClosed
      ) {
        await this.shippingService.loadKioskShipping();
        this.basketService.calculateBasket();
      } else {
        this.basketService.displayError(errors[0]);
        return;
      }
    }

    this.gtmService?.initiateCheckout();

    this.router.navigate([this.checkoutUrl]);
  }

  validateBasket() {
    this.showProductErrors = this.productHasErrors;
  }

  async submitDiscount() {
    const { promotionCode } = this.promotionForm.value;

    const isGiftCard = this.basketService.validateGiftCardCode(promotionCode?.trim());
    if (this.giftCardEnabled && isGiftCard) {
      this.basketService.addGiftcardCode(promotionCode?.trim());
    } else {
      const valid = await this.basketService.addDiscountCode(promotionCode);
      if (!valid) {
        this.promotionForm.controls.promotionCode.setErrors({ 'invalid-code': true });
        this.promotionForm.updateValueAndValidity();
        return;
      }
    }

    this.promotionForm.controls.promotionCode.setErrors(undefined);
    this.promotionForm.updateValueAndValidity();
  }
}
